import {
  GENERIC_ADMIN_AUTH_FAILURE,
  type AdminApiClient,
  type AdminBootstrap,
  type AdminLead,
  type AdminMembership,
  type AdminMembershipPlan,
  type AdminMembershipBillingAttempt,
  type MembershipBenefitRuleCreateInput,
  type MembershipPlanCreateInput,
  type AdminMarket,
  type AdminRecord,
  type AdminServiceArea,
  type AcquisitionSource,
  type SearchTermInsight,
  type AdminDeliveryAssignment,
  type AdminDirectListing,
  type AdminImportJob,
  type AdminStoreLocation,
} from './api-client.ts';

export interface AdminApiErrorShape {
  readonly code: string;
  readonly message: string;
  readonly requestId: string;
  readonly details?: unknown;
}

/** Traceable API failure. Screens distinguish this from an honest empty response. */
export class AdminApiError extends Error implements AdminApiErrorShape {
  readonly code: string;
  readonly requestId: string;
  readonly details?: unknown;

  constructor(error: AdminApiErrorShape) {
    super(error.message);
    this.name = 'AdminApiError';
    this.code = error.code;
    this.requestId = error.requestId;
    this.details = error.details;
  }
}

export interface AdminHttpClientOptions {
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  /**
   * Browser shells pause a rejected protected request while the user completes
   * MFA, then the original request is retried exactly once.
   */
  readonly onMfaRequired?: () => Promise<void>;
}

type ErrorEnvelope = {
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly details?: unknown;
  };
  readonly requestId?: unknown;
};

function endpoint(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/u, '')}${path}`;
}

function idempotencyKey(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `admin-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function errorFrom(value: unknown, status: number): AdminApiError {
  const envelope = (value ?? {}) as ErrorEnvelope;
  return new AdminApiError({
    code: typeof envelope.error?.code === 'string' ? envelope.error.code : `HTTP_${status}`,
    message:
      typeof envelope.error?.message === 'string'
        ? envelope.error.message
        : 'The administrator request could not be completed.',
    requestId:
      typeof envelope.requestId === 'string'
        ? envelope.requestId
        : 'request-id-not-returned-by-api',
    details: envelope.error?.details,
  });
}

function asRecord(value: unknown): AdminRecord {
  return value !== null && typeof value === 'object' ? (value as AdminRecord) : {};
}

function asServiceArea(value: unknown): AdminServiceArea {
  return value as AdminServiceArea;
}

function asMembership(value: unknown): AdminMembership {
  return asRecord(value) as AdminMembership;
}

function asMembershipPlan(value: unknown): AdminMembershipPlan {
  const record = asRecord(value) as unknown as AdminMembershipPlan & {
    readonly versions?: unknown;
  };
  return {
    ...record,
    versions: Array.isArray(record.versions)
      ? (record.versions.map(asRecord) as unknown as AdminMembershipPlan['versions'])
      : [],
  };
}

function asBillingAttempt(value: unknown): AdminMembershipBillingAttempt {
  return asRecord(value) as AdminMembershipBillingAttempt;
}

function asMarket(value: unknown): AdminMarket {
  return value as AdminMarket;
}

function asLead(value: unknown): AdminLead {
  return asRecord(value) as AdminLead;
}

function asAcquisitionSource(value: unknown): AcquisitionSource {
  return value as AcquisitionSource;
}

function asSearchTermInsight(value: unknown): SearchTermInsight {
  return value as SearchTermInsight;
}

function asDeliveryAssignment(value: unknown): AdminDeliveryAssignment {
  const record = asRecord(value) as unknown as AdminDeliveryAssignment & {
    readonly trackingEvents?: unknown;
    readonly courierEvents?: unknown;
  };
  return {
    ...record,
    trackingEvents: Array.isArray(record.trackingEvents)
      ? (record.trackingEvents.map(asRecord) as readonly AdminRecord[])
      : [],
    courierEvents: Array.isArray(record.courierEvents)
      ? (record.courierEvents.map(asRecord) as readonly AdminRecord[])
      : [],
  };
}

function asStoreLocation(value: unknown): AdminStoreLocation {
  return value as AdminStoreLocation;
}

function asImportJob(value: unknown): AdminImportJob {
  return value as AdminImportJob;
}

/**
 * HTTP implementation of the administrator contract. Collection handlers unwrap
 * their documented response envelope while individual endpoints retain their API body.
 */
export function createAdminHttpClient(options: AdminHttpClientOptions = {}): AdminApiClient {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const baseUrl = options.baseUrl ?? env?.VITE_ADMIN_API_BASE_URL ?? '';
  const fetchImpl = options.fetchImpl ?? fetch;

  async function request<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    body?: unknown,
    retryAfterMfa = true,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') headers['Idempotency-Key'] = idempotencyKey();
    const response = await fetchImpl(endpoint(baseUrl, path), {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const payload: unknown =
      response.status === 204 ? undefined : await response.json().catch(() => undefined);
    if (!response.ok) {
      const error = errorFrom(payload, response.status);
      if (
        error.code === 'PRECONDITION_FAILED' &&
        retryAfterMfa &&
        !path.startsWith('/api/v1/auth/mfa') &&
        options.onMfaRequired !== undefined
      ) {
        await options.onMfaRequired();
        return request(method, path, body, false);
      }
      throw error;
    }
    return payload as T;
  }

  return {
    auth: {
      async signIn(input) {
        try {
          const response = await request<{ readonly expiresAt?: unknown }>(
            'POST',
            '/api/v1/auth/session',
            input,
          );
          return typeof response.expiresAt === 'string'
            ? { ok: true }
            : {
                ok: false as const,
                kind: 'invalid_credentials' as const,
                message: GENERIC_ADMIN_AUTH_FAILURE,
              };
        } catch (error) {
          if (error instanceof AdminApiError && error.code === 'PRECONDITION_FAILED') {
            return {
              ok: false,
              kind: 'mfa_required',
              message:
                'Multi-factor authentication is required before you can access the administrator console.',
            };
          }
          return {
            ok: false,
            kind: 'invalid_credentials',
            message: GENERIC_ADMIN_AUTH_FAILURE,
          };
        }
      },
    },
    bootstrap: { get: () => request<AdminBootstrap>('GET', '/api/v1/admin/bootstrap') },
    mfa: {
      status: () => request('GET', '/api/v1/auth/mfa'),
      beginTotp: () => request('POST', '/api/v1/auth/mfa/totp/begin'),
      confirmTotp: (code) => request('POST', '/api/v1/auth/mfa/totp/confirm', { code }),
      stepUp: (code) => request('POST', '/api/v1/auth/mfa/step-up', { code }),
    },
    overview: {
      get: async () =>
        asRecord(
          (await request<{ readonly overview: unknown }>('GET', '/api/v1/admin/overview')).overview,
        ),
    },
    retailers: {
      applications: async () => {
        const payload = await request<unknown>('GET', '/api/v1/admin/retailers/applications');
        return Array.isArray(payload) ? payload.map(asRecord) : [];
      },
      approve: (retailerId) =>
        request('POST', `/api/v1/admin/retailers/${encodeURIComponent(retailerId)}/approve`),
      reject: (retailerId, reason) =>
        request('POST', `/api/v1/admin/retailers/${encodeURIComponent(retailerId)}/reject`, {
          reason,
        }),
      requestInfo: (applicationId, reason, fields) =>
        request(
          'POST',
          `/api/v1/admin/retailers/applications/${encodeURIComponent(applicationId)}/request-info`,
          { reason, fields },
        ),
    },
    users: {
      list: async (input = {}) => {
        const limit = input.limit ?? 25;
        const offset = input.offset ?? 0;
        const payload = await request<{ readonly users?: unknown }>(
          'GET',
          `/api/v1/admin/users?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}`,
        );
        return {
          users: Array.isArray(payload.users) ? payload.users.map(asRecord) : [],
          limit,
          offset,
        };
      },
      detail: (userId) => request('GET', `/api/v1/admin/users/${encodeURIComponent(userId)}`),
      grantRole: (userId, input) =>
        request('POST', `/api/v1/admin/users/${encodeURIComponent(userId)}/roles`, input),
      revokeRole: (userId, roleKey, reason) =>
        request(
          'POST',
          `/api/v1/admin/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleKey)}/revoke`,
          { reason },
        ),
      suspend: (userId, reason) =>
        request('POST', `/api/v1/admin/users/${encodeURIComponent(userId)}/suspend`, { reason }),
      reactivate: (userId, reason) =>
        request('POST', `/api/v1/admin/users/${encodeURIComponent(userId)}/reactivate`, {
          reason,
        }),
    },
    invitations: {
      list: async (input = {}) => {
        const limit = input.limit ?? 25;
        const offset = input.offset ?? 0;
        const status = input.status ? `&status=${encodeURIComponent(input.status)}` : '';
        const payload = await request<{ readonly invitations?: unknown }>(
          'GET',
          `/api/v1/admin/invitations?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}${status}`,
        );
        return {
          invitations: Array.isArray(payload.invitations) ? payload.invitations.map(asRecord) : [],
          limit,
          offset,
        };
      },
      create: (input) => request('POST', '/api/v1/admin/invitations', input),
      revoke: (invitationId, reason) =>
        request('POST', `/api/v1/admin/invitations/${encodeURIComponent(invitationId)}/revoke`, {
          reason,
        }),
    },
    flags: {
      list: async () => {
        const payload = await request<{ readonly flags?: unknown }>(
          'GET',
          '/api/v1/admin/feature-flags',
        );
        return Array.isArray(payload.flags) ? payload.flags.map(asRecord) : [];
      },
      update: (key, input) =>
        request('PATCH', `/api/v1/admin/feature-flags/${encodeURIComponent(key)}`, input),
    },
    audit: {
      list: async () => {
        const payload = await request<{ readonly entries?: unknown }>(
          'GET',
          '/api/v1/admin/audit-log?limit=25&offset=0',
        );
        return { entries: Array.isArray(payload.entries) ? payload.entries.map(asRecord) : [] };
      },
    },
    leads: {
      list: async () => {
        const [customer, retailer] = await Promise.all([
          request<{ readonly leads?: unknown }>('GET', '/api/v1/admin/leads/customer'),
          request<{ readonly leads?: unknown }>('GET', '/api/v1/admin/leads/retailer'),
        ]);
        return {
          customer: Array.isArray(customer.leads) ? customer.leads.map(asLead) : [],
          retailer: Array.isArray(retailer.leads) ? retailer.leads.map(asLead) : [],
        };
      },
      updateStatus: async (leadId, input) => {
        await request('PATCH', `/api/v1/admin/leads/${encodeURIComponent(leadId)}/status`, input);
      },
    },
    insights: {
      acquisition: async () => {
        const payload = await request<{ readonly sources?: unknown }>(
          'GET',
          '/api/v1/admin/analytics/acquisition',
        );
        return {
          sources: Array.isArray(payload.sources) ? payload.sources.map(asAcquisitionSource) : [],
        };
      },
      searchTerms: async () => {
        const payload = await request<{
          readonly topTerms?: unknown;
          readonly zeroResultTerms?: unknown;
        }>('GET', '/api/v1/admin/discovery/search-terms');
        return {
          topTerms: Array.isArray(payload.topTerms)
            ? payload.topTerms.map(asSearchTermInsight)
            : [],
          zeroResultTerms: Array.isArray(payload.zeroResultTerms)
            ? payload.zeroResultTerms.map(asSearchTermInsight)
            : [],
        };
      },
    },
    orders: {
      list: async (input = {}) => {
        const limit = input.limit ?? 25;
        const offset = input.offset ?? 0;
        const payload = await request<{ readonly orders?: unknown }>(
          'GET',
          `/api/v1/admin/orders?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}`,
        );
        return {
          orders: Array.isArray(payload.orders) ? payload.orders.map(asRecord) : [],
          limit,
          offset,
        };
      },
      detail: (orderId) => request('GET', `/api/v1/admin/orders/${encodeURIComponent(orderId)}`),
      createException: (orderId, input) =>
        request('POST', `/api/v1/admin/orders/${encodeURIComponent(orderId)}/exceptions`, input),
    },
    refunds: {
      list: async () => {
        const payload = await request<{ readonly returns?: unknown }>(
          'GET',
          '/api/v1/admin/refunds?limit=25&offset=0',
        );
        return { returns: Array.isArray(payload.returns) ? payload.returns.map(asRecord) : [] };
      },
      decide: (returnRequestId, status, reason) =>
        request('POST', `/api/v1/admin/returns/${encodeURIComponent(returnRequestId)}/decision`, {
          status,
          reason,
        }),
    },
    // Launch 1.0 operational exception queue. This is the surface the founder
    // actually uses; the refunds client above stays for the deferred release.
    exceptions: {
      list: async () => {
        const payload = await request<{ readonly exceptions?: unknown }>(
          'GET',
          '/api/v1/admin/order-exceptions?limit=25&offset=0',
        );
        return {
          exceptions: Array.isArray(payload.exceptions) ? payload.exceptions.map(asRecord) : [],
        };
      },
      resolve: (exceptionId, input) =>
        request(
          'POST',
          `/api/v1/admin/order-exceptions/${encodeURIComponent(exceptionId)}/resolve`,
          input,
        ),
    },
    delivery: {
      jobs: async ({ status, market, limit = 25, offset = 0 }) => {
        const query = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
          ...(status === undefined ? {} : { status }),
          ...(market === undefined ? {} : { market }),
        });
        const payload = await request<{
          readonly assignments?: unknown;
          readonly limit?: unknown;
          readonly offset?: unknown;
        }>('GET', `/api/v1/admin/delivery-jobs?${query.toString()}`);
        return {
          assignments: Array.isArray(payload.assignments)
            ? payload.assignments.map(asDeliveryAssignment)
            : [],
          limit: typeof payload.limit === 'number' ? payload.limit : limit,
          offset: typeof payload.offset === 'number' ? payload.offset : offset,
        };
      },
      detail: async (assignmentId) =>
        asDeliveryAssignment(
          await request('GET', `/api/v1/admin/delivery-jobs/${encodeURIComponent(assignmentId)}`),
        ),
    },
    directInventory: {
      listings: async (locationId) => {
        const payload = await request<{ readonly listings?: unknown }>(
          'GET',
          `/api/v1/admin/direct-inventory/listings${
            locationId === undefined ? '' : `?locationId=${encodeURIComponent(locationId)}`
          }`,
        );
        return Array.isArray(payload.listings)
          ? (payload.listings.map(asRecord) as unknown as readonly AdminDirectListing[])
          : [];
      },
    },
    stores: {
      list: async (input = {}) => {
        const query = new URLSearchParams();
        if (input.market !== undefined) query.set('market', input.market);
        if (input.status !== undefined) query.set('status', input.status);
        const suffix = query.size === 0 ? '' : `?${query.toString()}`;
        const payload = await request<{ readonly locations?: unknown }>(
          'GET',
          `/api/v1/admin/stores${suffix}`,
        );
        return Array.isArray(payload.locations) ? payload.locations.map(asStoreLocation) : [];
      },
    },
    imports: {
      jobs: async (input = {}) => {
        const query = new URLSearchParams();
        if (input.retailerId !== undefined) query.set('retailerId', input.retailerId);
        if (input.status !== undefined) query.set('status', input.status);
        if (input.quarantine !== undefined) query.set('quarantine', input.quarantine);
        query.set('limit', String(input.limit ?? 25));
        query.set('offset', String(input.offset ?? 0));
        const payload = await request<{
          readonly jobs?: unknown;
          readonly limit?: unknown;
          readonly offset?: unknown;
        }>('GET', `/api/v1/admin/imports?${query.toString()}`);
        return {
          jobs: Array.isArray(payload.jobs) ? payload.jobs.map(asImportJob) : [],
          limit: typeof payload.limit === 'number' ? payload.limit : (input.limit ?? 25),
          offset: typeof payload.offset === 'number' ? payload.offset : (input.offset ?? 0),
        };
      },
      releaseQuarantine: (importJobId) =>
        request<AdminImportJob>(
          'POST',
          `/api/v1/admin/imports/${encodeURIComponent(importJobId)}/release-quarantine`,
        ).then(asImportJob),
    },
    payouts: {
      list: async () => {
        const payload = await request<{ readonly payouts?: unknown }>(
          'GET',
          '/api/v1/admin/payouts',
        );
        return Array.isArray(payload.payouts) ? payload.payouts.map(asRecord) : [];
      },
      finalise: (payoutRunId) =>
        request('POST', `/api/v1/admin/payouts/${encodeURIComponent(payoutRunId)}/finalise`),
    },
    commissions: {
      get: () => request('GET', '/api/v1/admin/commission'),
      update: (input) => request('PATCH', '/api/v1/admin/commission', input),
    },
    membership: {
      listPlans: async () => {
        const payload = await request<{ readonly plans?: unknown }>(
          'GET',
          '/api/v1/admin/membership/plans',
        );
        return Array.isArray(payload.plans) ? payload.plans.map(asMembershipPlan) : [];
      },
      createPlan: (input: MembershipPlanCreateInput) =>
        request('POST', '/api/v1/admin/membership/plans', input),
      createBenefitRule: (input: MembershipBenefitRuleCreateInput) =>
        request('POST', '/api/v1/admin/membership/benefit-rules', input),
      listMemberships: async () => {
        const payload = await request<{ readonly memberships?: unknown }>(
          'GET',
          '/api/v1/admin/membership/memberships',
        );
        return Array.isArray(payload.memberships) ? payload.memberships.map(asMembership) : [];
      },
      billingAttempts: async (membershipId: string) => {
        const payload = await request<{ readonly attempts?: unknown }>(
          'GET',
          `/api/v1/admin/membership/memberships/${encodeURIComponent(membershipId)}/billing-attempts`,
        );
        return Array.isArray(payload.attempts) ? payload.attempts.map(asBillingAttempt) : [];
      },
    },
    markets: {
      list: async () => {
        const payload = await request<{ readonly markets?: unknown }>(
          'GET',
          '/api/v1/admin/markets',
        );
        return Array.isArray(payload.markets) ? payload.markets.map(asMarket) : [];
      },
      update: (marketId, input) =>
        request<AdminMarket>(
          'PATCH',
          `/api/v1/admin/markets/${encodeURIComponent(marketId)}`,
          input,
        ),
      createServiceArea: (marketId, input) =>
        request<AdminServiceArea>(
          'POST',
          `/api/v1/admin/markets/${encodeURIComponent(marketId)}/service-areas`,
          input,
        ).then(asServiceArea),
      updateServiceArea: (serviceAreaId, input) =>
        request<AdminServiceArea>(
          'PATCH',
          `/api/v1/admin/service-areas/${encodeURIComponent(serviceAreaId)}`,
          input,
        ).then(asServiceArea),
    },
  };
}
